import { useCallback } from "react";
import { QueryKey, useQuery } from "react-query";
import { useRouter } from "router";

import { urlPageConfigType } from "@/core/url";

export interface PageableQueryFunctionParams {
  page: number;
  size: number;
}

export type PageableQueryFunction<T> = (params: PageableQueryFunctionParams) => Promise<T>;

export const usePageableQuery = <T, TQueryKey extends QueryKey = QueryKey>(
  queryKey: TQueryKey,
  queryFunction: PageableQueryFunction<T>,
) => {
  const { searchParams, replaceSearchParams } = useRouter({
    ensureSearchParams: urlPageConfigType,
  });

  const page = searchParams.page;
  const size = searchParams.size;

  const query = useQuery([queryKey, size, page], () =>
    queryFunction({
      page,
      size,
    }),
  );

  const setPage = useCallback(
    (newPage: number) => {
      replaceSearchParams({ ...searchParams, page: newPage });
    },
    [searchParams, replaceSearchParams],
  );

  const setSize = useCallback(
    (newSize: number) => {
      replaceSearchParams({ ...searchParams, page: 0, size: newSize });
    },
    [searchParams, replaceSearchParams],
  );

  return {
    ...query,
    data: query.data!,
    setPage,
    setSize,
  };
};
